// AI Generated Component
import {
    Component,
    DestroyRef,
    OnInit,
    ViewEncapsulation,
    inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { UIRouterModule } from '@uirouter/angular';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import type { ChartData, ChartOptions } from 'chart.js';
import { ChartModule } from 'primeng/chart';
import { CardModule } from 'primeng/card';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ProgressBarModule } from 'primeng/progressbar';
import { TableModule } from 'primeng/table';
import { MessageModule } from 'primeng/message';
import moment from 'moment';
import { ParamService } from '@monsido/core/param/param.service';
import { SessionService } from '@monsido/core/session/session.service';
import { DateRangeService } from '@client/app/services/date-range/date-range.service';
import { StatsRepoService } from '@client/app/services/api/stats-repo/stats-repo.service';
import { DateTimeService } from '@client/app/services/date-time/date-time.service';
import { STATISTICS } from '@client/app/shared-modules/statistics/statistics.constant';
import {
    StatisticsDateRangeService,
} from '@monsido/modules/statistics/services/date-range/statistics-date-range.service';
import { IsStatsDemoDataPipe } from '@client/app/shared-modules/pipes/is-stats-demo-data.pipe';
import type { Domain } from '@monsido/modules/models/api/domain';
import { PageLayoutComponent } from '@client/app/feature-modules/shared/components/page-layout/page-layout.component';
import { MonNumberPipe } from '@client/app/shared-modules/pipes/mon-number.pipe';
import {
    SiteAnalyticsDateRangePickerComponent,
} from '@client/app/feature-modules/site-analytics/components/date-range-picker/date-range-picker.component';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type GenericData = Record<string, any>;

interface SummaryData {
    visits: number;
    visitors: number;
    pageviews: number;
    avg_time: string;
    bounce_rate: string;
}

interface MostFrequentEntry {
    title: string;
    label: string;
    value: number;
    total: number;
    type: string;
}

interface TrafficSourceCounts {
    organic: number;
    direct: number;
    referring: number;
    social: number;
    paid: number;
    total: number;
}

interface MostVisitedPage {
    url: string;
    label: string;
    visits: number;
    pageviews: number;
}

interface PerDateEntry {
    date: string;
    visits?: number;
    visitors?: number;
    pageviews?: number;
}

interface ApiSummaryResponse {
    visits?: number;
    visitors?: number;
    pageviews?: number;
    avg_time?: string;
    bounce_rate?: string;
}

@Component({
    selector: 'mon-statistics-summary',
    standalone: true,
    templateUrl: './statistics-summary.component.html',
    styleUrls: ['./statistics-summary.component.scss'],
    encapsulation: ViewEncapsulation.None,
    imports: [
        CommonModule,
        FormsModule,
        UIRouterModule,
        TranslocoModule,
        ChartModule,
        CardModule,
        ProgressSpinnerModule,
        ProgressBarModule,
        TableModule,
        MessageModule,
        PageLayoutComponent,
        SiteAnalyticsDateRangePickerComponent,
        IsStatsDemoDataPipe,
        MonNumberPipe,
    ],
})
export class StatisticsSummaryComponent implements OnInit {
    // Dependencies
    private readonly paramService = inject(ParamService);
    private readonly sessionService = inject(SessionService);
    private readonly dateRangeService = inject(DateRangeService);
    private readonly statisticsDateRangeService = inject(StatisticsDateRangeService);
    private readonly statsRepoService = inject(StatsRepoService);
    private readonly translocoService = inject(TranslocoService);
    private readonly dateTimeService = inject(DateTimeService);
    private readonly destroyRef = inject(DestroyRef);

    domain: Domain | null = null;
    primaryDateRange: [Date | null, Date | null] | null = null;
    loading = false;

    summaryData: SummaryData = {
        visits: 0,
        visitors: 0,
        pageviews: 0,
        avg_time: '00:00:00',
        bounce_rate: '0%',
    };

    totalVisitsLabel = '';
    totalVisitorsLabel = '';

    chartData: ChartData<'line'> = {
        labels: [],
        datasets: [],
    };
    chartOptions: ChartOptions<'line'> = {};

    trafficSourceCounts: TrafficSourceCounts = {
        organic: 0,
        direct: 0,
        referring: 0,
        social: 0,
        paid: 0,
        total: 0,
    };

    // Traffic source colors - matches chart colors
    readonly trafficSourceColors = {
        organic: '#71a3f6',
        direct: '#57B754',
        referring: '#EAA646',
        social: '#9500AC',
        paid: '#D24747',
    };
    trafficSourceChartData: ChartData<'bar'> = {
        labels: [],
        datasets: [],
    };
    trafficSourceChartOptions: ChartOptions<'bar'> = {};

    mostVisitedPages: MostVisitedPage[] = [];

    mostFrequentData: Record<string, MostFrequentEntry> = {};
    mostFrequentTypes = [
        { key: 'from', label: 'Location' },
        { key: 'using', label: 'Device' },
        { key: 'browser', label: 'Browser' },
        { key: 'os', label: 'Operating System' },
        { key: 'resolution', label: 'Resolution' },
        { key: 'language', label: 'Language' },
    ];
    allMostFrequentEmpty = false;
    emptyMostFrequentTypes: string[] = [];
    mostFrequentTypeLabels: Record<string, string> = {};

    private latestRequestId = 0;
    private trafficSourceRemoteData: Record<string, GenericData[]> = {
        organic: [],
        direct: [],
        referring: [],
        social: [],
        paid: [],
    };

    get hasTrafficSourceData (): boolean {
        return this.trafficSourceCounts.total > 0;
    }

    ngOnInit (): void {
        // Set up translation labels
        this.totalVisitsLabel = this.translocoService.translate('siteAnalytics.summary.sources.metrics.visits');
        this.totalVisitorsLabel = this.translocoService.translate('siteAnalytics.summary.sources.metrics.uniqueVisits');
        this.mostFrequentTypeLabels = {
            from: this.translocoService.translate('siteAnalytics.summary.general.location'),
            using: this.translocoService.translate('siteAnalytics.summary.general.device'),
            browser: this.translocoService.translate('siteAnalytics.summary.general.browser'),
            os: this.translocoService.translate('siteAnalytics.summary.general.operatingSystem'),
            resolution: this.translocoService.translate('siteAnalytics.summary.general.resolution'),
            language: this.translocoService.translate('siteAnalytics.summary.general.language'),
        };

        // Set up chart options
        this.setupChartOptions();
        this.setupTrafficSourceChartOptions();

        // Initialize date and load data
        const initialParams = this.paramService.getParams() as Record<string, unknown>;
        this.applyDateParams(initialParams, true);
        this.loadData();

        // Subscribe to param changes
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const deregisterHook = this.paramService.subscribeParamsChangeOnCurrentState((updated: any) => {
            this.applyDateParams(updated as Record<string, unknown>, false);
            this.loadData();
        }) as (() => void);

        this.destroyRef.onDestroy(() => {
            deregisterHook();
        });
    }

    onPrimaryDateRangeChange (value: [Date | null, Date | null] | null): void {
        this.primaryDateRange = value;

        if (!this.hasCompleteDateRange(value)) {
            return;
        }

        this.rememberDateRange();
        this.paramService.updateParams({
            from: this.toDateParam(value[0]),
            to: this.toDateParam(value[1]),
        });
    }

    getTrafficSourcePercentage (value: number): number {
        if (this.trafficSourceCounts.total <= 0) {
            return 0;
        }
        return (value * 100) / this.trafficSourceCounts.total;
    }

    /**
     * Returns PrimeIcons class for the most frequent entry type
     * Uses generic icons since PrimeIcons doesn't have browser-specific icons
     */
    getMostFrequentIcon (data: MostFrequentEntry): string {
        const label = data.label.toLowerCase();

        switch (data.type) {
            case 'browser':
                // PrimeIcons doesn't have browser-specific icons, use generic globe
                return 'pi pi-globe';
            case 'using':
                // Device type mapping
                if (label.includes('desktop')) {
                    return 'pi pi-desktop';
                }
                if (label.includes('tablet')) {
                    return 'pi pi-tablet';
                }
                if (label.includes('phone') || label.includes('smartphone') || label.includes('mobile')) {
                    return 'pi pi-mobile';
                }
                return 'pi pi-desktop';
            case 'os':
                // OS type mapping - use brand icons where available
                if (label.includes('android')) {
                    return 'pi pi-android';
                }
                if (label.includes('apple') || label.includes('ios') || label.includes('mac')) {
                    return 'pi pi-apple';
                }
                if (label.includes('windows')) {
                    return 'pi pi-desktop';
                }
                if (label.includes('linux')) {
                    return 'pi pi-desktop';
                }
                return 'pi pi-desktop';
            case 'from':
                return 'pi pi-globe';
            case 'language':
                return 'pi pi-language';
            case 'resolution':
                return 'pi pi-window-maximize';
            default:
                return 'pi pi-question-circle';
        }
    }

    private applyDateParams (params: Record<string, unknown>, syncIfMissing: boolean): void {
        const parsedRange = this.parseDateRange(params.from, params.to);

        if (parsedRange) {
            this.primaryDateRange = parsedRange;
            this.rememberDateRange();
            return;
        }

        const persisted = this.statisticsDateRangeService.getLastSelectedRange();
        const fallback = persisted ?? this.dateRangeService.setRangeDates();
        this.primaryDateRange = [
            fallback.startDate.toDate(),
            fallback.endDate.toDate(),
        ];

        if (!syncIfMissing) {
            return;
        }

        this.paramService.updateParams({
            from: this.toDateParam(fallback.startDate.toDate()),
            to: this.toDateParam(fallback.endDate.toDate()),
        });
    }

    private rememberDateRange (): void {
        if (!this.hasCompleteDateRange(this.primaryDateRange)) {
            return;
        }
        this.statisticsDateRangeService.setLastSelectedRange({
            startDate: moment(this.primaryDateRange[0]),
            endDate: moment(this.primaryDateRange[1]),
        });
    }

    private async loadData (): Promise<void> {
        if (!this.hasCompleteDateRange(this.primaryDateRange)) {
            return;
        }

        const requestId = ++this.latestRequestId;
        this.loading = true;

        this.domain = this.sessionService.domain;

        try {
            await Promise.all([
                this.loadSummaryData(),
                this.loadMostVisitedPages(),
                this.loadTrafficSources(),
                this.loadMostFrequent(),
            ]);
        } finally {
            if (requestId === this.latestRequestId) {
                this.loading = false;
            }
        }
    }

    private async loadSummaryData (): Promise<void> {
        const range = this.primaryDateRange as [Date, Date] | null;
        if (!range) {
            return;
        }
        const from = this.formatDate(range[0]);
        const to = this.formatDate(range[1]);

        const data = await this.statsRepoService.getSummary({
            from,
            to,
            type: 'total',
        }).catch(() => null) as ApiSummaryResponse | null;

        if (data) {
            this.summaryData = {
                visits: data.visits ?? 0,
                visitors: data.visitors ?? 0,
                pageviews: data.pageviews ?? 0,
                avg_time: data.avg_time ?? '00:00:00',
                bounce_rate: data.bounce_rate ?? '0%',
            };
        }

        // Load chart data
        const perDateData = await this.statsRepoService.getSummaryPerDate({ from, to }).catch(() => []) as PerDateEntry[];
        if (Array.isArray(perDateData)) {
            const labels: string[] = [];
            const visitsData: number[] = [];

            for (const entry of perDateData) {
                const formattedDate = this.dateTimeService.format(
                    this.dateTimeService.toUtcDate(entry.date),
                    'MMM D',
                );
                labels.push(formattedDate);
                visitsData.push(entry.visits ?? 0);
            }

            this.chartData = {
                labels,
                datasets: [
                    {
                        label: this.translocoService.translate('siteAnalytics.scaffold.table.column.visits'),
                        data: visitsData,
                        borderColor: '#50a6fb',
                        backgroundColor: 'rgba(80, 166, 251, 0.1)',
                        tension: 0,
                        fill: true,
                        pointRadius: 0,
                        pointHoverRadius: 0,
                    },
                ],
            };
        }
    }

    private async loadMostVisitedPages (): Promise<void> {
        const range = this.primaryDateRange as [Date, Date] | null;
        if (!range) {
            return;
        }
        const from = this.formatDate(range[0]);
        const to = this.formatDate(range[1]);

        const data = await this.statsRepoService.getMostPopularPages({
            from,
            to,
            limit: 7,
        }).catch(() => []) as { url?: string; label?: string; visits?: number; pageviews?: number; }[];

        if (Array.isArray(data)) {
            this.mostVisitedPages = data.map((page): MostVisitedPage => ({
                url: String(page.url ?? page.label ?? ''),
                label: String(page.label ?? ''),
                visits: window.Number(page.visits ?? 0),
                pageviews: window.Number(page.pageviews ?? 0),
            }));
        }
    }

    private async loadTrafficSources (): Promise<void> {
        const range = this.primaryDateRange as [Date, Date] | null;
        if (!range) {
            return;
        }
        const from = this.formatDate(range[0]);
        const to = this.formatDate(range[1]);

        const types = ['organic', 'direct', 'referring', 'social', 'paid'];
        const promises = types.map((type) =>
            this.statsRepoService.getVisitors({ from, to, type }).catch(() => []) as Promise<GenericData[]>,
        );

        const results = await Promise.all(promises);

        const counts: TrafficSourceCounts = {
            organic: 0,
            direct: 0,
            referring: 0,
            social: 0,
            paid: 0,
            total: 0,
        };

        const colors = [
            this.trafficSourceColors.organic,
            this.trafficSourceColors.direct,
            this.trafficSourceColors.referring,
            this.trafficSourceColors.social,
            this.trafficSourceColors.paid,
        ];

        results.forEach((data, index) => {
            const type = types[index];
            let visits = 0;

            this.trafficSourceRemoteData[type] = data;

            if (Array.isArray(data)) {
                for (const entry of data) {
                    visits += window.Number(entry.visits ?? 0);
                }
            }

            (counts as unknown as Record<string, number>)[type] = visits;
            counts.total += visits;
        });

        this.trafficSourceCounts = counts;

        // Build stacked bar chart data
        const dateMap = new Map<string, Map<string, number>>();

        types.forEach((type) => {
            const typeData = this.trafficSourceRemoteData[type];
            if (Array.isArray(typeData)) {
                typeData.forEach((entry) => {
                    const date = String(entry.date ?? '');
                    if (!dateMap.has(date)) {
                        dateMap.set(date, new Map<string, number>());
                    }
                    const dateCounts = dateMap.get(date);
                    if (dateCounts) {
                        dateCounts.set(type, window.Number(entry.visits ?? 0));
                    }
                });
            }
        });

        const sortedDates = [...dateMap.keys()].sort((a, b) => a.localeCompare(b));
        const chartLabels = sortedDates.map((date) => moment(date).format('MMM D'));

        const datasets = types.map((type, idx) => ({
            label: this.getTrafficSourceLabel(type),
            data: sortedDates.map((date) => dateMap.get(date)?.get(type) ?? 0),
            borderColor: colors[idx],
            backgroundColor: colors[idx],
            borderWidth: 1,
            stack: 'traffic-source-stack',
        }));

        this.trafficSourceChartData = {
            labels: chartLabels,
            datasets,
        };
    }

    private async loadMostFrequent (): Promise<void> {
        const range = this.primaryDateRange as [Date, Date] | null;
        if (!range) {
            return;
        }
        const from = this.formatDate(range[0]);
        const to = this.formatDate(range[1]);
        const domainId = (this.sessionService.params as GenericData)?.domainId as string | number | undefined;

        const params: GenericData = {
            from,
            to,
            limit: 100,
            page: 1,
            domainId,
        };

        const [locations, devices, browsers, os, resolutions, languages] = await Promise.all<GenericData[]>([
            this.statsRepoService.getLocations(params).catch(() => []),
            this.statsRepoService.getDevices(params).catch(() => []),
            this.statsRepoService.getBrowsers(params).catch(() => []),
            this.statsRepoService.getOs(params).catch(() => []),
            this.statsRepoService.getResolutions(params).catch(() => []),
            this.statsRepoService.getLanguages(params).catch(() => []),
        ]);

        const sources = [
            { key: 'from', data: locations },
            { key: 'using', data: devices },
            { key: 'browser', data: browsers },
            { key: 'os', data: os },
            { key: 'resolution', data: resolutions },
            { key: 'language', data: languages },
        ];

        let emptyCount = 0;
        this.emptyMostFrequentTypes = [];

        sources.forEach(({ key, data }) => {
            const title = this.mostFrequentTypeLabels[key];
            const entries = data && Array.isArray(data) ? data : [];
            const firstEntry = entries[0] as GenericData | undefined;

            if (firstEntry) {
                const total = entries.reduce((sum: number, item: unknown) => {
                    const i = item as GenericData;
                    return sum + Number.parseInt(String(i.visits ?? 0), 10);
                }, 0);

                this.mostFrequentData[key] = {
                    title,
                    label: String(firstEntry.label ?? this.translocoService.translate('siteAnalytics.summary.general.unknown')),
                    value: window.Number(firstEntry.visits ?? 1),
                    total: total || 1,
                    type: key,
                };
            } else {
                this.emptyMostFrequentTypes.push(key);
                this.mostFrequentData[key] = {
                    title,
                    label: this.translocoService.translate('siteAnalytics.summary.general.unknown'),
                    value: 1,
                    total: 1,
                    type: key,
                };
                emptyCount++;
            }
        });

        this.allMostFrequentEmpty = emptyCount === sources.length;
    }

    private setupChartOptions (): void {
        this.chartOptions = {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: {
                    display: false,
                },
            },
            scales: {
                x: {
                    grid: {
                        display: false,
                    },
                },
                y: {
                    beginAtZero: true,
                    ticks: {
                        callback: (value): string | null => {
                            const num = window.Number(value ?? 0);
                            // Only show ticks for whole numbers
                            return num === Math.floor(num) ? num.toString() : null;
                        },
                    },
                },
            },
        };
    }

    private setupTrafficSourceChartOptions (): void {
        this.trafficSourceChartOptions = {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: {
                    position: 'bottom',
                },
                tooltip: {
                    callbacks: {
                        label: (ctx): string => {
                            const label = String(ctx.dataset.label ?? '');
                            const value = window.Number(ctx.raw ?? 0);
                            return `${label}: ${value.toLocaleString()}`;
                        },
                    },
                },
            },
            scales: {
                x: {
                    stacked: true,
                    grid: {
                        display: false,
                    },
                },
                y: {
                    stacked: true,
                    beginAtZero: true,
                    ticks: {
                        callback: (value): string | null => {
                            const num = window.Number(value ?? 0);
                            // Only show ticks for whole numbers
                            return num === Math.floor(num) ? num.toString() : null;
                        },
                    },
                },
            },
        };
    }

    private getTrafficSourceLabel (type: string): string {
        switch (type) {
            case 'organic':
                return this.translocoService.translate('siteAnalytics.summary.sources.referring.searchEngines');
            case 'direct':
                return this.translocoService.translate('siteAnalytics.summary.sources.series.directVisits');
            case 'referring':
                return this.translocoService.translate('siteAnalytics.summary.sources.series.referringVisits');
            case 'social':
                return this.translocoService.translate('siteAnalytics.summary.sources.series.socialVisits');
            case 'paid':
                return this.translocoService.translate('siteAnalytics.summary.sources.series.campaignVisits');
            default:
                return type;
        }
    }

    private formatDate (date: Date | null): string {
        if (!date) {
            return moment().format(STATISTICS.DATE_FORMAT);
        }
        return moment(date).format(STATISTICS.DATE_FORMAT);
    }

    private toDateParam (value: Date | null): string {
        if (!value) {
            return moment().format(STATISTICS.DATE_FORMAT);
        }
        return moment(value).format(STATISTICS.DATE_FORMAT);
    }

    private parseDateRange (from: unknown, to: unknown): [Date, Date] | null {
        if (typeof from !== 'string' || typeof to !== 'string' || !from || !to) {
            return null;
        }

        const fromDate = moment(from, STATISTICS.DATE_FORMAT, true);
        const toDate = moment(to, STATISTICS.DATE_FORMAT, true);

        if (!fromDate.isValid() || !toDate.isValid()) {
            return null;
        }

        return [fromDate.toDate(), toDate.toDate()];
    }

    private hasCompleteDateRange (value: [Date | null, Date | null] | null): value is [Date, Date] {
        return Boolean(value && value[0] instanceof Date && value[1] instanceof Date);
    }
}
